import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { useNoticeBannersStackContext } from '@sg/garnish';

import {
  type ApplyPromoCodeParams,
  type ApplyPromoCodeResponse,
  type BagReward,
} from '@order/features-ordering';

import {
  BagLoyaltyRewardsDocument,
  type BagLoyaltyRewardsQuery,
} from '../../GraphQL/BagLoyaltyRewards.generated';
import {
  ApplyPromoCodeDocument,
  type ApplyPromoCodeMutation,
  type ApplyPromoCodeMutationVariables,
} from '../../GraphQL/BagPromoCode.generated';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for applying promo codes in the bag.
 */
export const usePromoCode = () => {
  const client = useClient();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  // ─── Mutations ────────────────────────────────────────────────────────────

  const applyPromoCode = useMemo(
    () =>
      fromPromise<ApplyPromoCodeResponse, ApplyPromoCodeParams>(async ({ input }) => {
        const mutation = client.mutation<ApplyPromoCodeMutation, ApplyPromoCodeMutationVariables>;

        const response = await mutation(
          ApplyPromoCodeDocument,
          { input },
          { requestPolicy: 'network-only' },
        ).toPromise();

        const result = response.data?.submitPromoOrGiftCardCode.__typename;

        if (result === 'SubmitPromoCodeSuccessV2' || result === 'SubmitGiftCardCodeSuccess') {
          const queryRewards = client.query<BagLoyaltyRewardsQuery>;

          const rewardsResponse = await queryRewards(
            BagLoyaltyRewardsDocument,
            {},
            { requestPolicy: 'network-only' },
          ).toPromise();

          const nonPointsRewards = rewardsResponse.data?.cart?.availableRewards ?? []; // prettier-ignore
          const pointsRewards = rewardsResponse.data?.cart?.eligiblePointRewards ?? []; // prettier-ignore
          const rewards = [...nonPointsRewards, ...pointsRewards] as BagReward[]; // prettier-ignore

          addNoticeBanner({
            text: formatMessage(messages.promoCodeApplied),
            palette: 'success',
          });

          return {
            rewards,
            success: true,
            error: undefined,
            isRewardMissingSku: false,
          };
        }

        if (result === 'PromoCodeAlreadyApplied') {
          return {
            rewards: [],
            success: false,
            error: formatMessage(messages.promoCodeAlreadyApplied),
            isRewardMissingSku: false,
          };
        }

        if (result === 'InvalidPromoCode') {
          return {
            rewards: [],
            success: false,
            error: formatMessage(messages.invalidPromoCode),
            isRewardMissingSku: false,
          };
        }

        return {
          rewards: [],
          success: false,
          error: formatMessage(messages.validationError),
          isRewardMissingSku: false,
        };
      }),
    [client.mutation, client.query, formatMessage, addNoticeBanner],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { applyPromoCode };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  promoCodeApplied: {
    defaultMessage:
      'Promo code was successfully redeemed. A new reward has been added to your account.',
    description: 'Bag | Promo code | Promo code applied',
  },
  promoCodeAlreadyApplied: {
    defaultMessage: 'The promo code you entered has already been redeemed.',
    description: 'Bag | Promo code | Promo code already applied',
  },
  invalidPromoCode: {
    defaultMessage: 'Your promo code could not be added, it is invalid.',
    description: 'Bag | Promo code | Invalid promo code',
  },
  validationError: {
    defaultMessage: 'Your promo code could not be added, check if it is correct.',
    description: 'Bag | Promo code | Validation error',
  },
});
